import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, CheckCircle2, ArrowLeft } from "lucide-react";
import { API } from "@/constants/config";
import { useLang } from "@/constants/i18n";

const inputClass =
    "w-full border-b border-ink/20 bg-transparent py-3 text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-burgundy";

export const ReviewForm = ({ showBackLink = false }) => {
    const { t, lang } = useLang();
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [form, setForm] = useState({ nombre: "", ubicacion: "", resena: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        if (rating === 0) {
            setError(t.review.ratingError);
            return;
        }
        setSending(true);
        setError("");
        try {
            await axios.post(`${API}/reviews`, { ...form, rating, lang });
            setDone(true);
        } catch (err) {
            setError(t.review.submitError);
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="flex h-full flex-col items-center justify-center py-10 text-center"
                data-testid="review-success"
            >
                <CheckCircle2 className="h-12 w-12 text-forest" />
                <h3 className="mt-6 font-serif text-3xl font-light text-ink">
                    {t.review.successTitle}
                </h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-ink/70">
                    {t.review.successText}
                </p>
                {showBackLink && (
                    <Link
                        to="/"
                        data-testid="review-back-button"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-burgundy px-8 py-3.5 text-sm tracking-wide text-ivory transition-colors duration-300 hover:bg-forest"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {t.review.backHome}
                    </Link>
                )}
            </motion.div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="review-form">
            <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ink/60">
                    {t.review.ratingLabel}
                </p>
                <div className="mt-3 flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            data-testid={`review-star-${n}`}
                            onClick={() => setRating(n)}
                            onMouseEnter={() => setHover(n)}
                            onMouseLeave={() => setHover(0)}
                            className="transition-transform duration-200 hover:scale-110"
                            aria-label={`${n} estrellas`}
                        >
                            <Star
                                className={`h-9 w-9 transition-colors duration-200 ${
                                    n <= (hover || rating) ? "fill-gold text-gold" : "text-ink/20"
                                }`}
                            />
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-7 space-y-6">
                <div>
                    <label htmlFor="review-nombre" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                        {t.review.nameLabel}
                    </label>
                    <input
                        id="review-nombre"
                        data-testid="review-nombre-input"
                        required
                        minLength={2}
                        value={form.nombre}
                        onChange={set("nombre")}
                        placeholder={t.review.namePh}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label htmlFor="review-ubicacion" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                        {t.review.locationLabel}
                    </label>
                    <input
                        id="review-ubicacion"
                        data-testid="review-ubicacion-input"
                        value={form.ubicacion}
                        onChange={set("ubicacion")}
                        placeholder={t.review.locationPh}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label htmlFor="review-resena" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                        {t.review.reviewLabel}
                    </label>
                    <textarea
                        id="review-resena"
                        data-testid="review-resena-input"
                        required
                        minLength={10}
                        maxLength={1000}
                        rows={4}
                        value={form.resena}
                        onChange={set("resena")}
                        placeholder={t.review.reviewPh}
                        className={`${inputClass} resize-none`}
                    />
                </div>
            </div>

            {error && (
                <p data-testid="review-error" className="mt-5 text-sm text-burgundy">
                    {error}
                </p>
            )}

            <button
                type="submit"
                disabled={sending}
                data-testid="review-submit-button"
                className="mt-8 w-full rounded-full bg-burgundy py-4 text-sm tracking-wide text-ivory transition-[background-color,opacity] duration-300 hover:bg-forest disabled:opacity-60"
            >
                {sending ? t.review.sending : t.review.submit}
            </button>
            <p className="mt-4 text-center text-xs text-ink/45">{t.review.fine}</p>
        </form>
    );
};

export default ReviewForm;
